import React from 'react';
import { MdTimer, MdUpdate, MdPlayArrow } from 'react-icons/md';

interface ResumeSessionModalProps {
    isOpen: boolean;
    missedTimeSeconds: number;
    gapDetectionTime?: number | null;
    onContinue: () => void;
    onSync: () => void;
}

const ResumeSessionModal: React.FC<ResumeSessionModalProps> = ({
    isOpen,
    missedTimeSeconds,
    gapDetectionTime,
    onContinue,
    onSync,
}) => {
    // Track current time to update elapsed duration live
    const [currentTime, setCurrentTime] = React.useState(Date.now());

    React.useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isOpen) {
            setCurrentTime(Date.now()); // Sync on open
            interval = setInterval(() => {
                setCurrentTime(Date.now());
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isOpen]);

    if (!isOpen) return null;

    // Calculate total missed time: initial gap + time spent in modal
    const additionalMissed = (gapDetectionTime && currentTime > gapDetectionTime)
        ? Math.floor((currentTime - gapDetectionTime) / 1000)
        : 0;
    const totalMissedSeconds = missedTimeSeconds + additionalMissed;

    const formatMissedTime = (seconds: number) => {
        if (seconds < 60) return `${seconds} seconds`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        if (remainingSeconds === 0) return `${minutes} minute${minutes > 1 ? 's' : ''}`;
        return `${minutes}m ${remainingSeconds}s`;
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full border border-gray-200 shadow-xl transform transition-all">
                <div className="text-center space-y-6">
                    {/* Icon */}
                    <div className="flex justify-center">
                        <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center border border-orange-100">
                            <MdTimer className="text-3xl text-orange-500" />
                        </div>
                    </div>

                    {/* Title & Message */}
                    <div className="space-y-2">
                        <h3 className="text-2xl font-semibold text-gray-900">
                            Welcome Back
                        </h3>
                        <p className="text-gray-600">
                            You were away for <span className="font-medium text-orange-600">{formatMissedTime(totalMissedSeconds)}</span>.
                            <br />
                            How would you like to proceed?
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                        <button
                            onClick={onContinue}
                            className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-medium transition-all group"
                        >
                            <MdPlayArrow className="text-xl group-hover:scale-110 transition-transform" />
                            <div className="text-left">
                                <div className="text-sm font-semibold">Resume from here</div>
                                <div className="text-xs opacity-90 font-light">Continue timer where it stopped</div>
                            </div>
                        </button>

                        <button
                            onClick={onSync}
                            className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-white border-2 border-orange-100 hover:border-orange-200 text-gray-700 hover:text-orange-600 rounded-xl font-medium transition-all group"
                        >
                            <MdUpdate className="text-xl text-gray-400 group-hover:text-orange-500 transition-colors" />
                            <div className="text-left">
                                <div className="text-sm font-semibold">Update timer</div>
                                <div className="text-xs text-gray-400 group-hover:text-orange-500/70">Subtract missed time</div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResumeSessionModal;
